/**
 * The tester guide (part 11b, table M): one source for the Help dialog and `docs/guide.md`. The guide is a list of blocks
 * naming catalogue messages (decision 34: every string through i18n); a guide message is Markdown's inline text with
 * three marks only, `code`, **strong** and *emphasis*, so the file holds each message as it is and the dialog makes an
 * element of each mark (M1, M2). What the file must not read as markup is the catalogue test's to refuse (M3).
 */
import { EN } from '../i18n/en.ts';
import type { Messages } from '../i18n/en.ts';
import { createTranslator } from '../i18n/i18n.ts';
import type { Catalog, Translate } from '../i18n/i18n.ts';
import { guideVersion } from './version.ts';

/** A guide message's key. */
export type GuideKey = Extract<keyof Messages, `guide.${string}`>;

/** One block of the guide (M1): a section heading, a paragraph, a list, or a two-column table with its head. */
export type GuideBlock<T = GuideKey> =
  | { readonly kind: 'heading'; readonly text: T }
  | { readonly kind: 'paragraph'; readonly text: T }
  | { readonly kind: 'list'; readonly items: readonly T[] }
  | { readonly kind: 'table'; readonly head: readonly [T, T]; readonly rows: readonly (readonly [T, T])[] };

/** The guide's title, the dialog's heading and the file's first line. */
export const GUIDE_TITLE: GuideKey = 'guide.title';

/** The guide, in reading order (the tester guide's sections, the user's request 2026-10-06). */
export const GUIDE: readonly GuideBlock[] = Object.freeze([
  { kind: 'paragraph', text: 'guide.intro' },
  { kind: 'heading', text: 'guide.start.heading' },
  { kind: 'list', items: ['guide.start.address', 'guide.start.sync', 'guide.start.bundle', 'guide.start.plain', 'guide.start.ios'] },
  { kind: 'heading', text: 'guide.books.heading' },
  { kind: 'list', items: ['guide.books.book', 'guide.books.chapter', 'guide.books.trash', 'guide.books.split', 'guide.books.import', 'guide.books.reimport', 'guide.books.bundle', 'guide.books.details'] },
  { kind: 'heading', text: 'guide.format.heading' },
  { kind: 'list', items: ['guide.format.pages', 'guide.format.templates', 'guide.format.linked', 'guide.format.heading2', 'guide.format.subtitle', 'guide.format.image'] },
  { kind: 'heading', text: 'guide.writing.heading' },
  { kind: 'list', items: ['guide.writing.save', 'guide.writing.enter', 'guide.writing.click'] },
  { kind: 'paragraph', text: 'guide.writing.marks' },
  {
    kind: 'table',
    head: ['guide.marks.type', 'guide.marks.result'],
    rows: [
      ['guide.marks.italic', 'guide.marks.italicResult'],
      ['guide.marks.bold', 'guide.marks.boldResult'],
      ['guide.marks.smallCaps', 'guide.marks.smallCapsResult'],
      ['guide.marks.heading', 'guide.marks.headingResult'],
      ['guide.marks.list', 'guide.marks.listResult'],
      ['guide.marks.quote', 'guide.marks.quoteResult'],
      ['guide.marks.link', 'guide.marks.linkResult'],
    ],
  },
  { kind: 'list', items: ['guide.writing.images', 'guide.writing.words', 'guide.writing.notes', 'guide.writing.find', 'guide.writing.undo', 'guide.writing.plain'] },
  { kind: 'heading', text: 'guide.preview.heading' },
  { kind: 'list', items: ['guide.preview.pane', 'guide.preview.book', 'guide.preview.print', 'guide.preview.phone', 'guide.preview.pdf', 'guide.preview.epub', 'guide.preview.docx', 'guide.preview.bundle'] },
  { kind: 'heading', text: 'guide.settings.heading' },
  { kind: 'list', items: ['guide.settings.editor', 'guide.settings.keys', 'guide.settings.focus'] },
  { kind: 'heading', text: 'guide.good.heading' },
  { kind: 'list', items: ['guide.good.help', 'guide.good.offline', 'guide.good.tabs', 'guide.good.updates', 'guide.good.attention', 'guide.good.privacy'] },
  { kind: 'heading', text: 'guide.later.heading' },
  { kind: 'paragraph', text: 'guide.later.body' },
] as const);

/** The guide's blocks with each message in `t`'s language. */
export function guideBlocks(t: Translate<Messages>): readonly GuideBlock<string>[] {
  return GUIDE.map((block): GuideBlock<string> => {
    switch (block.kind) {
      case 'heading':
      case 'paragraph':
        return { kind: block.kind, text: t(block.text) };
      case 'list':
        return { kind: 'list', items: block.items.map((item) => t(item)) };
      case 'table':
        return { kind: 'table', head: [t(block.head[0]), t(block.head[1])], rows: block.rows.map(([left, right]) => [t(left), t(right)] as const) };
    }
  });
}

/** A run of a guide message (M2): text, or the text inside one mark. */
export interface Segment {
  readonly kind: 'text' | 'code' | 'strong' | 'em';
  readonly text: string;
}

/** Each mark's delimiter, longest first, so `**` is read before `*`. */
const MARKS: readonly (readonly [string, Segment['kind']])[] = [
  ['`', 'code'],
  ['**', 'strong'],
  ['*', 'em'],
];

/**
 * A guide message's runs (M2): a mark runs from its delimiter to the next same delimiter, holding at least one character,
 * and holds its text as it is (no mark inside another); a delimiter with no closer is text. The runs, each put back with
 * its delimiters, are the message.
 */
export function inline(text: string): readonly Segment[] {
  const runs: Segment[] = [];
  let plain = '';
  let at = 0;
  while (at < text.length) {
    const mark = MARKS.find(([delimiter]) => text.startsWith(delimiter, at));
    if (mark !== undefined) {
      const [delimiter, kind] = mark;
      const start = at + delimiter.length;
      const end = text.indexOf(delimiter, start + 1);
      if (end !== -1) {
        if (plain !== '') runs.push({ kind: 'text', text: plain });
        plain = '';
        runs.push({ kind, text: text.slice(start, end) });
        at = end + delimiter.length;
        continue;
      }
    }
    plain += text.charAt(at);
    at += 1;
  }
  if (plain !== '') runs.push({ kind: 'text', text: plain });
  return runs;
}

/** A row of a Markdown pipe table. */
function row(cells: readonly string[]): string {
  return `| ${cells.join(' | ')} |`;
}

/** The guide as Markdown (M1): the title, then each block, a blank line between blocks; each message as it is. */
export function guideMarkdown(t: Translate<Messages>): string {
  const blocks = guideBlocks(t).map((block) => {
    switch (block.kind) {
      case 'heading':
        return `## ${block.text}`;
      case 'paragraph':
        return block.text;
      case 'list':
        return block.items.map((item) => `- ${item}`).join('\n');
      case 'table':
        return [row(block.head), '|---|---|', ...block.rows.map((cells) => row(cells))].join('\n');
    }
  });
  return `${[`# ${t(GUIDE_TITLE)}`, ...blocks].join('\n\n')}\n`;
}

/** Each translator's guide version, worked out once. */
const versions = new WeakMap<Translate<Messages>, string>();

/** V1, V2: the version of the guide `t` shows: it changes whenever the guide's text in that language does. */
export function shownVersion(t: Translate<Messages>): string {
  const known = versions.get(t);
  if (known !== undefined) return known;
  const version = guideVersion(guideMarkdown(t));
  versions.set(t, version);
  return version;
}

/** Where `pnpm guide` writes the guide, from the workspace root. */
export const GUIDE_PATH = 'docs/guide.md';

/** The English guide's file, as `pnpm guide` writes it (M5). */
export function guideFile(): string {
  return guideMarkdown(createTranslator<Messages & Catalog>(EN, 'en'));
}
